import React from "react"
import { useIntl } from "@components/Intl"
import { Box } from "@components/Layout"
import { Paragraph, ParagraphTitle } from "@components/Typography"

interface Props {
  uid: string
  prodName: string
  articlesId: string[]
  colName?: string | null
  material?: string
}

const SeoDescription: React.FC<Props> = ({
  uid,
  prodName,
  colName,
  material,
  articlesId,
}) => {
  const { locale, messages } = useIntl()
  const productCat = {
    nl: {
      rings: {
        single: "Ring",
        plural: "Ringen",
      },
      bracelets: {
        single: "Armband",
        plural: "Armbanden",
      },
      necklaces: {
        single: "Collier",
        plural: "Colliers",
      },
      earrings: "Oorbellen",
      gemstones: "Stenen",
    },
    en: {
      rings: {
        single: "Ring",
        plural: "Rings",
      },
      bracelets: {
        single: "Bracelet",
        plural: "Bracelets",
      },
      necklaces: {
        single: "Necklace",
        plural: "Necklaces",
      },
      earrings: "Earrings",
      gemstones: "Gemstones",
    },
  }

  const materials = {
    ceramic: "Ceramic",
    diamonds: "Diamonds",
    rosegold: "Rosé Gold",
    whitegold: "White Gold",
    yellowgold: "Yellow Gold",
  }

  const collectionName = colName
    ? messages.properties.style.options[colName]
    : ""
  const transformIds: string[] = [
    "8757232000",
    "8757231900",
    "8756232000",
    "8756231900",
    "8751018803",
    "8751018802",
    "8751018801",
    "8751018800",
  ]

  function includesTransformIds(ids: string[]): boolean {
    let included = false
    for (const id of ids) {
      if (transformIds.includes(id)) {
        included = true
      }
    }

    return included
  }

  function getCarats(ids: string[]) {
    const includes = includesTransformIds(ids)
    if (includes) {
      return "14"
    } else {
      return "18"
    }
  }

  return (
    <Box position="relative" width="100%">
      <ParagraphTitle color="text.dark">
        {messages.global.aboutProduct}
      </ParagraphTitle>
      <Paragraph
        color="text.light"
        style={{ fontSize: "14px", lineHeight: "1.4" }}
      >
        {uid === "rings" || uid === "bracelets" || uid === "necklaces" ? (
          locale === "nl" ? (
            <>
              De {prodName}{" "}
              {productCat[locale][uid] && productCat[locale][uid].single}
              {material &&
                materials[material] &&
                ` van ${materials[material]}`}{" "}
              sluit prachtig aan bij de stijlvolle juwelengarderobe van Choices
              by DL. Zowel de kleursteen, het frame, als de base is
              verwisselbaar. Daardoor kunt u met onze exclusieve{" "}
              {getCarats(articlesId)} karaats gouden juwelen oneindige
              combinaties maken. Dit zorgt voor ultieme flexibiliteit in de
              juwelengarderobe van Choices by DL. <br /> <br />
              Deze interchangeable jewellery maakt gebruik van een ingenieus
              systeem dat is gepatenteerd. Dit maakt Choices by DL een uniek
              onderdeel van GASSAN Diamonds, de grootste juwelier van Amsterdam.
            </>
          ) : (
            <>
              The {prodName}{" "}
              {productCat[locale][uid] && productCat[locale][uid].single}
              {material &&
                materials[material] &&
                ` of ${materials[material]}`}{" "}
              matches perfect with the stylish juwellery wardrobe of Choices by
              DL. As well the color stone, the frame, as the base are
              interchangeable. This allows you to make infinite combinations
              with our exclusive {getCarats(articlesId)} carat gold jewellery.
              It results in an ultimate flexibility in the jewellery wardrobe of
              Choices by DL. <br /> <br />
              This interchangeable jewellery uses an ingenious patented system,
              and makes Choices by DL a unique part of GASSAN Diamonds, the
              biggest jewelry store of Amsterdam.
            </>
          )
        ) : uid === "earrings" ? (
          locale === "nl" ? (
            <>
              De {prodName} {productCat[locale][uid] && productCat[locale][uid]}
              {material &&
                materials[material] &&
                ` van ${materials[material]}`}{" "}
              sluiten prachtig aan bij de stijlvolle juwelengarderobe van
              Choices by DL. Zowel de kleurstenen als de base zijn
              verwisselbaar. Daardoor kunt u met onze exclusieve{" "}
              {getCarats(articlesId)} karaats gouden juwelen oneindige
              combinaties maken. Dit zorgt voor ultieme flexibiliteit in de
              juwelengarderobe van Choices by DL. <br /> <br /> Deze
              interchangeable jewellery maakt gebruik van een ingenieus systeem
              dat is gepatenteerd. Dit maakt Choices by DL een uniek onderdeel
              van GASSAN Diamonds, de grootste juwelier van Amsterdam.
            </>
          ) : (
            <>
              The {prodName}{" "}
              {productCat[locale][uid] && productCat[locale][uid]}
              {material &&
                materials[material] &&
                ` of ${materials[material]}`}{" "}
              matches perfect with the stylish juwellery wardrobe of Choices by
              DL. As well color stones as the base are interchangeable. This
              allows you to make endless combinations with our exclusive{" "}
              {getCarats(articlesId)} carat gold jewellery. It results in an
              ultimate flexibility in the jewellery wardrobe of Choices by DL.{" "}
              <br /> <br /> This interchangeable jewellery uses an ingenious
              patented system, and makes Choices by DL a unique part of GASSAN
              Diamonds, the biggest jewelry store of Amsterdam.
            </>
          )
        ) : uid === "gemstones" ? (
          locale === "nl" ? (
            colName === "hippiechic" ? (
              <>
                De juwelen van Choices by DL zijn zowel bij de kleursteen, het
                frame, als de base verwisselbaar. Daardoor kunt u met onze
                exclusieve {getCarats(articlesId)} karaats gouden juwelen
                oneindige combinaties maken. Dit zorgt voor ultieme
                flexibiliteit in de juwelengarderobe van Choices by DL. Deze{" "}
                {prodName} stenen behoren tot de
                {collectionName} collectie. Deze kleurstenen zijn te dragen op
                de {`${collectionName} ${productCat[locale].earrings}`}
                . Hiermee sluiten deze kleurstenen prachtig aan bij de
                stijlvolle juwelengarderobe van Choices by DL. <br /> <br />
                Deze interchangeable jewellery maakt gebruik van een ingenieus
                systeem dat is gepatenteerd. Dit maakt Choices by DL een uniek
                onderdeel van GASSAN Diamonds, de grootste juwelier van
                Amsterdam.
              </>
            ) : (
              <>
                De juwelen van Choices by DL zijn zowel bij de kleursteen, het
                frame, als de base verwisselbaar. Daardoor kunt u met onze
                exclusieve {getCarats(articlesId)} karaats gouden juwelen
                oneindige combinaties maken. Dit zorgt voor ultieme
                flexibiliteit in de juwelengarderobe van Choices by DL. De{" "}
                {prodName} steen behoort tot de {collectionName} collectie. Deze
                kleursteen is te dragen op de{" "}
                {`${collectionName} ${productCat[locale].rings.plural}, ${collectionName} ${productCat[locale].bracelets.plural} en ${collectionName} ${productCat[locale].necklaces.plural}`}
                . Hiermee sluit deze kleursteen prachtig aan bij de stijlvolle
                juwelengarderobe van Choices by DL.
                <br /> <br />
                Deze interchangeable jewellery maakt gebruik van een ingenieus
                systeem dat is gepatenteerd. Dit maakt Choices by DL een uniek
                onderdeel van GASSAN Diamonds, de grootste juwelier van
                Amsterdam.
              </>
            )
          ) : colName === "hippiechic" ? (
            <>
              The jewellery of Choices by DL are both with the color stone, the
              frame, as the base interchangeable. This allows you to make
              endless combinations with our exclusive {getCarats(articlesId)}{" "}
              carat gold jewellery. It results in an ultimate flexibility in the
              jewellery wardrobe of Choices by DL. The {prodName} stone is part
              of the {collectionName} collection. These color stones can be
              combined with the{" "}
              {`${collectionName} ${productCat[locale].earrings}`}, suiting
              perfectly with the stylish jewellery wardrobe of Choices by DL.{" "}
              <br /> <br />
              This interchangeable jewellery uses an ingenious patented system,
              and makes Choices by DL a unique part of GASSAN Diamonds, the
              biggest jewelry store of Amsterdam.
            </>
          ) : (
            <>
              The jewellery of Choices by DL are both with the color stone, the
              frame, as the base interchangeable. This allows you to make
              endless combinations with our exclusive {getCarats(articlesId)}{" "}
              carat gold jewellery. It results in an ultimate flexibility in the
              jewellery wardrobe of Choices by DL. The {prodName} stone is part
              of the {collectionName} collection. This color stone can be
              combined with the{" "}
              {`${collectionName} ${productCat[locale].rings.plural}, ${collectionName} ${productCat[locale].bracelets.plural} en ${collectionName} ${productCat[locale].necklaces.plural}`}
              , suiting perfectly with the stylish jewellery wardrobe of Choices
              by DL.
              <br /> <br /> This interchangeable jewellery uses an ingenious
              patented system, and makes Choices by DL a unique part of GASSAN
              Diamonds, the biggest jewelry store of Amsterdam.
            </>
          )
        ) : null}
      </Paragraph>
    </Box>
  )
}

export default SeoDescription
